import { useEffect, useState } from 'react';
import { zoomAutoCaptureAPI } from '../services/api';

/**
 * Zoom auto-capture — org-admin toggle.
 *
 * When on, Rithmo auto-joins connected users' scheduled Zoom meetings (via meeting.started
 * webhooks) to capture + analyze them (transcript, recap, commitments). Renders in
 * Settings → Integrations, next to Slack Huddles.
 */
export default function ZoomAutoCaptureToggle() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = async () => {
    try {
      const res = await zoomAutoCaptureAPI.getStatus();
      setStatus(res?.data ?? res);
    } catch (e) {
      setError(e?.message || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const onToggle = async () => {
    setError('');
    setSaving(true);
    try {
      await zoomAutoCaptureAPI.setEnabled(!status?.enabled);
      await refresh();
    } catch (e) {
      setError(e?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-400">Loading Zoom auto-capture…</div>;

  const enabled = Boolean(status?.enabled);
  const isAdmin = Boolean(status?.isOrgAdmin);
  const connectedCount = Number(status?.connectedZoomUsers ?? 0);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Auto-capture Zoom meetings</h3>
          <p className="mt-1 text-sm text-gray-500">
            When on, Rithmo automatically joins connected users’ <strong>scheduled</strong> Zoom meetings and
            captures them — transcript, recap, and commitments — with no manual step. {connectedCount} user
            {connectedCount === 1 ? '' : 's'} connected.
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
            enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {enabled ? 'On' : 'Off'}
        </span>
      </div>

      {!isAdmin && (
        <p className="mt-4 text-sm text-amber-600">Only an org admin can change this.</p>
      )}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      {isAdmin && (
        <div className="mt-4">
          <button
            onClick={onToggle}
            disabled={saving}
            className={`rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60 ${
              enabled
                ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {saving ? 'Saving…' : enabled ? 'Turn off auto-capture' : 'Turn on auto-capture'}
          </button>
        </div>
      )}
    </div>
  );
}
